import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import {
  Package,
  AlertTriangle,
  Warehouse,
  Euro,
  TrendingUp,
  ArrowRight,
} from "lucide-react";

function MetricCard({ label, value, sub, color = "#F97316", testid, icon: Icon }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-zinc-200 rounded-sm p-5 relative overflow-hidden"
      style={{ borderLeft: `4px solid ${color}` }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-zinc-400" strokeWidth={2} />}
      </div>
      <div className="font-display text-4xl font-mono-num leading-none">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-2">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [movements, setMovements] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get("/movements").then((r) => setMovements(r.data || [])).catch(() => setMovements([]));
  }, []);

  const fmt = (n) =>
    new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      n || 0
    );

  return (
    <div className="max-w-[1600px] mx-auto space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
            ÜBERSICHT · STAND IN ECHTZEIT
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase">Dashboard</h1>
        </div>
        <Link
          to="/artikel/neu"
          data-testid="dashboard-new-article-cta"
          className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-5 h-11 rounded-sm font-semibold text-sm tracking-wide"
        >
          + NEUER ARTIKEL
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          testid="metric-articles"
          label="Artikel gesamt"
          value={stats?.total_articles ?? "–"}
          sub="Unterschiedliche Positionen"
          icon={Package}
        />
        <MetricCard
          testid="metric-quantity"
          label="Bestand Hauptlager"
          value={stats?.stock_main ?? "–"}
          sub={`+ ${stats?.stock_buffer ?? 0} Zwischen · ${stats?.stock_pickup ?? 0} Abhol`}
          color="#09090B"
          icon={TrendingUp}
        />
        <MetricCard
          testid="metric-low-stock"
          label="Niedriger Bestand"
          value={stats?.low_stock_count ?? "–"}
          sub="Unter Mindestmenge"
          color="#DC2626"
          icon={AlertTriangle}
        />
        <MetricCard
          testid="metric-shelves"
          label="Regale"
          value={stats?.shelves_count ?? "–"}
          sub="Auf Lagerplan"
          color="#16A34A"
          icon={Warehouse}
        />
      </div>

      {(stats?.stock_pickup > 0 || stats?.pending_users > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {stats?.stock_pickup > 0 && (
            <Link
              to="/abhollager"
              data-testid="dashboard-pickup-cta"
              className="bg-white border border-zinc-200 rounded-sm p-5 hover:bg-zinc-50 transition-colors flex items-center justify-between"
              style={{ borderLeft: "4px solid #9333EA" }}
            >
              <div>
                <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">ABHOLLAGER</div>
                <div className="font-display text-2xl uppercase mt-1">
                  {stats.stock_pickup} Stk. wartet auf Abholung
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  Schnellansicht öffnen — gruppiert nach Projekt.
                </div>
              </div>
              <ArrowRight className="w-6 h-6" />
            </Link>
          )}
          {stats?.pending_users > 0 && (
            <Link
              to="/benutzer"
              data-testid="dashboard-pending-users-cta"
              className="bg-yellow-50 border border-yellow-300 rounded-sm p-5 hover:bg-yellow-100 transition-colors flex items-center justify-between"
              style={{ borderLeft: "4px solid #CA8A04" }}
            >
              <div>
                <div className="mono text-[10px] tracking-[0.25em] text-yellow-800">FREIGABEN</div>
                <div className="font-display text-2xl uppercase mt-1">
                  {stats.pending_users} neue Benutzer warten
                </div>
                <div className="text-xs text-yellow-800 mt-1">
                  Auf Freigabe durch Admin.
                </div>
              </div>
              <ArrowRight className="w-6 h-6 text-yellow-800" />
            </Link>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div data-testid="metric-ek" className="bg-white border border-zinc-200 rounded-sm p-6" style={{ borderLeft: "4px solid #F97316" }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">EINKAUFSWERT</div>
              <div className="font-display text-3xl mt-1 font-mono-num">€ {fmt(stats?.total_ek_value)}</div>
            </div>
            <Euro className="w-6 h-6 text-zinc-400" />
          </div>
          <div className="text-xs text-zinc-500">Summe EK × Menge über alle Artikel.</div>
        </div>

        <div data-testid="metric-vk" className="bg-white border border-zinc-200 rounded-sm p-6" style={{ borderLeft: "4px solid #16A34A" }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">VERKAUFSWERT</div>
              <div className="font-display text-3xl mt-1 font-mono-num">€ {fmt(stats?.total_vk_value)}</div>
            </div>
            <Euro className="w-6 h-6 text-zinc-400" />
          </div>
          <div className="text-xs text-zinc-500">Summe VK × Menge über alle Artikel.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">UNTER MINDESTMENGE</div>
              <div className="font-display text-lg uppercase mt-0.5">Nachbestellen</div>
            </div>
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div className="p-2">
            {(stats?.low_stock_items?.length ?? 0) === 0 ? (
              <div className="text-sm text-zinc-500 px-3 py-6 text-center">
                Alle Bestände im grünen Bereich.
              </div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {stats?.low_stock_items?.map((a) => (
                    <tr key={a.id} className="border-b border-zinc-100 last:border-0">
                      <td className="px-3 py-2.5">
                        <div className="font-medium">{a.name}</div>
                        <div className="mono text-[11px] text-zinc-500">{a.ean}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="font-mono-num font-semibold text-red-600">
                          {a.quantity} / {a.min_quantity}
                        </div>
                        <div className="mono text-[10px] text-zinc-500">IST / MIN</div>
                      </td>
                      <td className="px-3 py-2.5 text-right w-12">
                        <Link
                          to={`/artikel/${a.id}`}
                          className="inline-flex items-center justify-center w-8 h-8 hover:bg-zinc-100 rounded-sm"
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-200">
            <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">JÜNGSTE BEWEGUNGEN</div>
            <div className="font-display text-lg uppercase mt-0.5">Verlauf</div>
          </div>
          <div className="divide-y divide-zinc-100 max-h-[400px] overflow-y-auto">
            {movements.length === 0 ? (
              <div className="text-sm text-zinc-500 px-5 py-6 text-center">
                Noch keine Bewegungen.
              </div>
            ) : (
              movements.slice(0, 10).map((m) => (
                <div key={m.id} className="px-5 py-3 flex items-center gap-3">
                  <div
                    className={`mono text-sm font-bold w-12 text-center py-1 rounded-sm ${
                      m.delta > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}
                  >
                    {m.delta > 0 ? `+${m.delta}` : m.delta}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{m.article_name}</div>
                    <div className="mono text-[10px] text-zinc-500">
                      {new Date(m.at).toLocaleString("de-DE")}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
